import {memo, useMemo} from 'react';

import {LetterAvatar} from '@/app/shared-components/LetterAvatar';
import {COLORS} from '@/styles/colors';

interface SplitResultParticipantsProps {
  participants: string[];
  split: Record<string, number>;
  isPayer: (name: string) => boolean;
  participantLabel: (name: string) => string;
}

const PAYER_AVATAR_BG = COLORS.BACKGROUND_ON_TERTIARY_CONTAINER;
const PAYER_AVATAR_TEXT = COLORS.BLUE_500;

interface SplitResultParticipantRowProps {
  name: string;
  amount: number;
  isPayer: boolean;
  label: string;
}

const SplitResultParticipantRow = memo(
  ({name, amount, isPayer, label}: SplitResultParticipantRowProps) => (
    <div className="split-result-participant-row">
      <LetterAvatar
        name={name}
        size={40}
        bgColor={isPayer ? PAYER_AVATAR_BG : undefined}
        textColor={isPayer ? PAYER_AVATAR_TEXT : undefined}
      />
      <span className="split-result-participant-name">{label}</span>
      <span className="split-result-participant-amount">
        ${amount.toFixed(2)}
      </span>
    </div>
  ),
);

export const SplitResultParticipants = memo(
  ({
    participants,
    split,
    isPayer,
    participantLabel,
  }: SplitResultParticipantsProps) => {
    const sortedParticipants = useMemo(() => {
      return [...participants].sort((a, b) => {
        if (isPayer(a)) return 1;
        if (isPayer(b)) return -1;
        return 0;
      });
    }, [participants, isPayer]);

    return (
      <div className="split-result-participants">
        {sortedParticipants.map((name, index) => (
          <SplitResultParticipantRow
            key={`${name}-${index}`}
            name={name}
            amount={split[name] ?? 0}
            isPayer={isPayer(name)}
            label={participantLabel(name)}
          />
        ))}
      </div>
    );
  },
);
